import { useEffect } from 'react'

import { BROADCAST_CHANNEL, type BROADCAST_TOPIC } from '@src/utils/constants.util'
import { log } from '@src/utils/log.util'

const namespace = '[BroadcastChannel]'

type BroadcastMessage = {
  topic: BROADCAST_TOPIC
  payload?: unknown
}

type SubscriptionCallback = (payload?: unknown) => void

// Global variables to be shared across all hook instances
let channel: BroadcastChannel | null = null
const subscriptions = new Map<BROADCAST_TOPIC, SubscriptionCallback[]>()

/**
 * Custom hook for BroadcastChannel API
 * @returns {Object} - An object with publish and subscribe methods
 * @returns {Function} publish - Publishes a message to the channel
 * @returns {Function} subscribe - Subscribes to a topic and calls the callback when a message is received
 * @example
 * const { publish, subscribe } = useBroadcastChannel()
 * publish('topic ', { message: 'Hello, world!' })
 * subscribe('topic', (payload) => { console.log(payload) })
 */
export const useBroadcastChannel = () => {
  useEffect(() => {
    // Initialize the channel if it doesn't exist
    if (!channel) {
      channel = new BroadcastChannel(BROADCAST_CHANNEL)

      // Set up message listener
      const handleMessage = (event: MessageEvent<BroadcastMessage>) => {
        const { topic, payload } = event.data
        const callbacks = subscriptions.get(topic)
        callbacks?.forEach((callback) => callback(payload))
        log.debug(`${namespace} Received message from topic: "${topic}"`)
      }

      channel.addEventListener('message', handleMessage)
    }

    return () => {
      // Clean up only when no more subscriptions exist
      if (subscriptions.size === 0 && channel) {
        channel.close()
        channel = null
      }
    }
  }, [])

  const publish = (topic: BROADCAST_TOPIC, payload?: unknown) => {
    if (!channel) return

    const message: BroadcastMessage = { topic, payload }
    channel.postMessage(message)
    log.debug(`${namespace} Published message to topic: "${topic}"`, message)
  }

  const subscribe = (topic: BROADCAST_TOPIC, callback: SubscriptionCallback) => {
    const callbacks = subscriptions.get(topic) ?? []
    subscriptions.set(topic, [...callbacks, callback])

    // Return unsubscribe function
    return () => {
      const callbacks = subscriptions.get(topic) ?? []
      subscriptions.set(
        topic,
        callbacks.filter((cb) => cb !== callback)
      )
    }
  }

  return {
    publish,
    subscribe,
  }
}
